import { useState, useEffect, useRef } from "react";
import "./RankingComponent.css";
import { useGameContext } from '../GameContext';
import { SUBTITLE_RESTRICTION, TIMERS_GAME } from '../../utils/constants';
import placeholderLogo from '../../assets/logo-game.png';
import PlayerCard from "../PlayerCard/PlayerCard";
import Confetti from "react-confetti";
import { useNavigate } from "react-router-dom";


import { ANIMATIONS } from "../../utils/constants";

import soundTrackRanking from "../../assets/audios/RankingSound.mp3";
import soundTrackWinner from "../../assets/audios/WinnerSoundtrack.mp3";
import winnerSound from "../../assets/audios/Winner.mp3";
import popSound from "../../assets/audios/pop.mp3";
import celebrationSound from "../../assets/audios/Celebration.mp3";
import CloseCaptionButton from "../Buttons/CloseCaptionButton";
import config from "../../utils/config";
import InGameHeader from "../in-game/InGameHeader";


const RankingComponent = ({ playAnimation }) => {
  const { game, user, socket } = useGameContext();
  const player = game.players.find(player => player.id === user);
  const [currentPhrase, setCurrentPhrase] = useState(0);
  const audioRef = useRef(null); // Referencia para el audio

  const player_state = player ? player.status : "in_ranking";
  const timePercentage = ((game.timeToStart) / TIMERS_GAME.timeInRanking) * 100;

  const [finalMessage, setFinalMessage] = useState("Y el ganador es…");
  const [showConfetti, setShowConfetti] = useState(false);
  const effectExecuted = useRef(false); // Ref para evitar múltiples ejecuciones
  const [bestPlayer, setBestPlayer] = useState({});

  const divRef = useRef();
  const [showRanking, setShowRanking] = useState(false);

  useEffect(() => {
    const savedMuted = localStorage.getItem("isMuted");
    const isMutedFromStorage = savedMuted === "true";

    if (audioRef.current) {
      audioRef.current.muted = isMutedFromStorage;
    }

    if (game.gameName.toLowerCase() === SUBTITLE_RESTRICTION.toLowerCase()) {
      setIsCCActive(localStorage.getItem("subtitles") === "true");
    }
  }, [game.gameName]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "isMuted" && audioRef.current) {
        audioRef.current.muted = event.newValue === "true";
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);


  const navigate = useNavigate();

  const playSoundPop = () => {
    const sound = new Audio(popSound);
    sound.play();
  };

  const playSoundCelebration = () => {
    const sound = new Audio(celebrationSound);
    sound.playbackRate = 1.5;
    sound.play();
  };

  const phrases = [
    ["", .5],
    ["", 3, ANIMATIONS.reading],
    ["", 2, ANIMATIONS.celebrating],
    ["", 2, ANIMATIONS.celebrating],
    ["", 2, ANIMATIONS.celebrating],
    ["", 2.5, ANIMATIONS.talking],
  ]

  const playSoundWinner = () => {
    const sound = new Audio(winnerSound);
    sound.play();
  };

  function nextLine(phraseIndex) {

    // If we've reached the last phrase, stop.
    if (phraseIndex >= phrases.length - 1) {
      return;
    }

    const newPhrase = phraseIndex + 1;
    const delayInSeconds = phrases[newPhrase][1];

    // Update the phrase.
    setCurrentPhrase(newPhrase);
    playAnimation(phrases[newPhrase][2]);

    // Schedule the next update using the new phrase index.
    setTimeout(() => nextLine(newPhrase), delayInSeconds * 1000);
  }

  const t = useRef(null);

  useEffect(() => {
    if (t.current) return;

    // Start after a delay that gives the first phrase extra time.
    if (game.state !== 'final_ranking')
      return;

    playAnimation(phrases[0][2]);
    const initialDelay = phrases[0][1] * 1000;
    t.current = setTimeout(() => {
      nextLine(0);
    }, initialDelay);
  }, [game.state]);

  const determineWinner = (players) => {
    if (!players || players.length === 0) return null;

    // Ordenamos primero por score y luego por timeLeft
    const sortedPlayers = [...players].sort((a, b) => {
      if (a.score !== b.score) {
        return b.score - a.score;
      }
      const aTotalTimeLeft = a.answers.reduce((sum, answer) => sum + (answer.timeLeft || 0), 0);
      const bTotalTimeLeft = b.answers.reduce((sum, answer) => sum + (answer.timeLeft || 0), 0);
      return bTotalTimeLeft - aTotalTimeLeft;
    });

    const topPlayer = sortedPlayers[0];
    const secondPlayer = sortedPlayers[1];

    // Verificamos si hay un empate absoluto
    if (
      secondPlayer &&
      topPlayer.score === secondPlayer.score &&
      topPlayer.answers.reduce((sum, ans) => sum + (ans.timeLeft || 0), 0) ===
      secondPlayer.answers.reduce((sum, ans) => sum + (ans.timeLeft || 0), 0)
    ) {
      return null; // Nadie gana en caso de empate absoluto
    }

    return topPlayer;
  };


  useEffect(() => {
    if (game.state !== 'final_ranking' || effectExecuted.current) return;
    effectExecuted.current = true;

    const winner = determineWinner(game.players);
    setBestPlayer(winner || {});

    if (winner && winner.id === user) {
      divRef.current.classList.remove('lobby');
      setFinalMessage(
        <div className="flex flex-col gap-1 items-center justify-center">
          <div>
            Felicidades por ganar esta partida a:
          </div>
          <div className="text-2xl font-bold">
            ¡{winner.playerName}!
          </div>
        </div>
      );
      setShowConfetti(true);
      playSoundCelebration();
      playSoundWinner();
      setShowRanking(true);
      divRef.current.classList.add('lobby');
    } else {
      setShowRanking(true);
      setFinalMessage("El juego ha finalizado. ¡Vuelve pronto!");
    }
  }, [game.state]);


  const sortPlayers = (a, b) => {
    if (a.score !== b.score) {
      return b.score - a.score;
    }
    const aTotalTimeLeft = a.answers.reduce((sum, answer) => sum + (answer.timeLeft || 0), 0);
    const bTotalTimeLeft = b.answers.reduce((sum, answer) => sum + (answer.timeLeft || 0), 0);
    return bTotalTimeLeft - aTotalTimeLeft;
  };

  const playerIsReady = game.players.some((p) => p.id === user && p.status === "ready");

  const handleRedirect = () => {
    playSoundPop();
    window.location.href = `${config.host_remote}/create-room`;
  };

  const [isCCActive, setIsCCActive] = useState(false);
  const toggleCC = () => {
    const newValue = localStorage.getItem("subtitles") === "true" ? "false" : "true";
    localStorage.setItem("subtitles", newValue);
    setIsCCActive(newValue === "true");
  };

  return (
    <div ref={divRef} className="lobby">
      <audio ref={audioRef} autoPlay controls loop style={{
        position: 'absolute',
        zIndex: 100,
        opacity: 0
      }}>
        <source src={(showConfetti && user === bestPlayer.id) ? soundTrackWinner : soundTrackRanking} type="audio/mp3" />
      </audio>
      {showConfetti && user === bestPlayer.id && <Confetti gravity={.1} initialVelocityY={{ min: 15, max: 30 }} numberOfPieces={50} />}
      {/* Player List */}
      <div className="lobbyContainer">
        {/* Header */}
        {(game.state !== 'final_ranking' || (game.state === 'final_ranking' && showRanking)) && <InGameHeader
          brandLogo={game.companyLogo ? encodeURI(game.companyLogo) : placeholderLogo}
          title={game.gameName}
          subtitle={game.moduleName}
        />}
        {game.state !== 'final_ranking' && <div className="contPlayer">
          {Array.from({ length: Math.max(game.playersToStart, game.players.length) })
            .map((_, index) => game.players[index])
            .filter(Boolean)
            .sort(sortPlayers)
            .map((player, index) => (
              <PlayerCard key={player.id} player={player} isRanking={true} />
            ))}
        </div>}
        {game.state === 'final_ranking' && showRanking && <div className="contPlayer">
          {Array.from({ length: Math.max(game.playersToStart, game.players.length) })
            .map((_, index) => game.players[index])
            .filter(Boolean)
            .sort(sortPlayers)
            .map((player, index) => (
              <PlayerCard key={player.id} player={player} isRanking={true} displayReadyCheck={false} />
            ))}
        </div>}



        <div className="generalContainerBorder">
          <div className="generalContainer">
            <label className="textTimer">{game.state === 'ranking' ? '¡Sigamos jugando!' : finalMessage}</label>
          </div>
        </div>

        {game.state === 'ranking' ? <button className="ConfirmButton"
          onClick={() => {
            playSoundPop();
            socket.emit("player_ready", { gameId: game.id, playerId: player.id });
          }}
          disabled={playerIsReady}>
          <div>
            ¡Listo!
          </div>
        </button>
          : null}

        {game.state === 'final_ranking' && showRanking && <button className="ConfirmButton"
          onClick={handleRedirect}>
          <div>
            Revisar avance
          </div>
        </button>}
      </div>
      {game.gameName === SUBTITLE_RESTRICTION
        && <CloseCaptionButton isActive={isCCActive} onClick={toggleCC} />}
    </div>

  );
};

export default RankingComponent;